import { useState, useEffect } from "react";
import {
  Key, Copy, Eye, EyeOff, Building2, BarChart3,
  Download, Trash2, RefreshCw, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { API, apiCall, formatDate } from "@/lib/api";

const maskKey = (key) => key ? `${key.slice(0, 10)}${"*".repeat(20)}${key.slice(-6)}` : "";

export const ApiTab = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [newKeyLimit, setNewKeyLimit] = useState("");
  const [selectedKeyUsage, setSelectedKeyUsage] = useState(null);
  const [visibleKeys, setVisibleKeys] = useState({});
  const [keyToDelete, setKeyToDelete] = useState(null);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const { ok, data } = await apiCall("GET", `${API}/keys`);
      if (ok) setApiKeys(data.keys || []);
    } catch {
      console.error("Error fetching API keys");
    }
  };

  const handleCreateKey = async () => {
    if (!newCompanyName.trim()) return toast.error("Ingresa el nombre de la empresa");
    try {
      const { ok, data } = await apiCall("POST", `${API}/keys`, {
        company_name: newCompanyName.trim(),
        limit_per_month: newKeyLimit ? parseInt(newKeyLimit) : null
      });
      if (!ok) throw new Error("Failed");
      toast.success(`API Key creada para "${data.company_name}"`);
      setNewCompanyName("");
      setNewKeyLimit("");
      fetchApiKeys();
    } catch {
      toast.error("Error al crear API key");
    }
  };

  const handleDeleteKey = async (keyId) => {
    try {
      await apiCall("DELETE", `${API}/keys/${keyId}`);
      toast.success("API Key eliminada");
      setKeyToDelete(null);
      if (selectedKeyUsage?.key?.key_id === keyId) setSelectedKeyUsage(null);
      fetchApiKeys();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const handleToggleKey = async (keyId, currentActive) => {
    try {
      await apiCall("PATCH", `${API}/keys/${keyId}`, { is_active: !currentActive });
      toast.success(currentActive ? "Key desactivada" : "Key activada");
      fetchApiKeys();
    } catch {
      toast.error("Error al actualizar");
    }
  };

  const loadKeyUsage = async (keyId) => {
    try {
      const { ok, data } = await apiCall("GET", `${API}/keys/${keyId}/usage`);
      if (ok) setSelectedKeyUsage(data);
    } catch {
      toast.error("Error al cargar uso");
    }
  };

  const handleDownloadDocs = () => {
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = `${API}/keys/docs/pdf`;
    document.body.appendChild(iframe);
    setTimeout(() => document.body.removeChild(iframe), 30000);
    toast.success("Descargando documentación...");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado al portapapeles");
  };

  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-6 backdrop-blur-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-md bg-violet-600 flex items-center justify-center">
              <Key className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Nueva API Key</h2>
              <p className="text-sm text-zinc-400">Crea claves para empresas externas</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold text-zinc-300 flex items-center gap-2 mb-2">
                <Building2 className="w-4 h-4 text-violet-400" />Nombre de Empresa
              </Label>
              <Input
                data-testid="company-name-input"
                value={newCompanyName}
                onChange={e => setNewCompanyName(e.target.value)}
                placeholder="Ej: CRM Solutions SA"
                className="bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-500"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-zinc-300 flex items-center gap-2 mb-2">
                <BarChart3 className="w-4 h-4 text-violet-400" />Limite Mensual (vacío = ilimitado)
              </Label>
              <Input
                data-testid="key-limit-input"
                value={newKeyLimit}
                onChange={e => setNewKeyLimit(e.target.value.replace(/\D/g, ""))}
                placeholder="Ej: 10000"
                className="bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-500"
              />
            </div>
            <Button
              data-testid="create-key-btn"
              onClick={handleCreateKey}
              disabled={!newCompanyName.trim()}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white"
            >
              <Key className="w-4 h-4 mr-2" />Crear API Key
            </Button>
            <Button
              data-testid="download-docs-btn"
              onClick={handleDownloadDocs}
              variant="outline"
              className="w-full bg-zinc-950 border-zinc-800 text-zinc-300 hover:bg-zinc-800 hover:text-white"
            >
              <Download className="w-4 h-4 mr-2" />Descargar Documentación PDF
            </Button>
          </div>
        </div>

        <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-6 backdrop-blur-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Key className="w-5 h-5 text-violet-400" />API Keys ({apiKeys.length})
            </h3>
            <Button variant="ghost" size="icon" onClick={fetchApiKeys} className="text-zinc-400 hover:text-white">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          <ScrollArea className="h-80">
            <div className="space-y-3">
              {apiKeys.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <Key className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay API keys creadas</p>
                </div>
              ) : (
                apiKeys.map(k => (
                  <div
                    key={k.key_id}
                    data-testid={`api-key-${k.key_id}`}
                    className={`p-4 rounded-lg border transition-colors cursor-pointer ${
                      selectedKeyUsage?.key?.key_id === k.key_id
                        ? "bg-violet-600/10 border-violet-500/30"
                        : "bg-zinc-950 border-zinc-800 hover:border-zinc-700"
                    }`}
                    onClick={() => loadKeyUsage(k.key_id)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-violet-400" />
                        <span className="font-medium text-white">{k.company_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={k.is_active ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-zinc-700 text-zinc-400"}>
                          {k.is_active ? "Activa" : "Inactiva"}
                        </Badge>
                        <Button
                          variant="ghost" size="icon"
                          onClick={e => { e.stopPropagation(); handleToggleKey(k.key_id, k.is_active); }}
                          className="text-zinc-500 hover:text-amber-400 h-6 w-6"
                          title={k.is_active ? "Desactivar" : "Activar"}
                        >
                          {k.is_active ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        </Button>
                        <Button
                          variant="ghost" size="icon"
                          onClick={e => { e.stopPropagation(); setKeyToDelete(k); }}
                          className="text-zinc-500 hover:text-rose-400 h-6 w-6"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <code className="text-xs text-zinc-500 font-mono flex-1 truncate">
                        {visibleKeys[k.key_id] ? k.key : maskKey(k.key)}
                      </code>
                      <Button
                        variant="ghost" size="icon"
                        onClick={e => { e.stopPropagation(); setVisibleKeys(prev => ({ ...prev, [k.key_id]: !prev[k.key_id] })); }}
                        className="text-zinc-500 hover:text-white h-5 w-5"
                      >
                        {visibleKeys[k.key_id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                      </Button>
                      <Button
                        variant="ghost" size="icon"
                        onClick={e => { e.stopPropagation(); copyToClipboard(k.key); }}
                        className="text-zinc-500 hover:text-white h-5 w-5"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-zinc-500">
                      <span>Mes: {k.usage_this_month || 0} req</span>
                      <span>Total: {k.usage_total || 0} req</span>
                      <span>Limite: {k.limit_per_month || "Sin limite"}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      </div>

      {selectedKeyUsage && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="p-4 rounded-lg bg-zinc-900/50 border border-white/10">
              <p className="text-xs font-semibold tracking-widest uppercase text-zinc-500 mb-1">Requests (mes)</p>
              <p className="text-2xl font-bold text-white">{selectedKeyUsage.monthly_stats?.total_requests || 0}</p>
            </div>
            <div className="p-4 rounded-lg bg-zinc-900/50 border border-white/10">
              <p className="text-xs font-semibold tracking-widest uppercase text-zinc-500 mb-1">Teléfonos (mes)</p>
              <p className="text-2xl font-bold text-violet-400">{selectedKeyUsage.monthly_stats?.total_phones || 0}</p>
            </div>
            <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <p className="text-xs font-semibold tracking-widest uppercase text-emerald-400 mb-1">Celulares</p>
              <p className="text-2xl font-bold text-emerald-400">{selectedKeyUsage.monthly_stats?.total_celular || 0}</p>
            </div>
            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <p className="text-xs font-semibold tracking-widest uppercase text-blue-400 mb-1">Fijos</p>
              <p className="text-2xl font-bold text-blue-400">{selectedKeyUsage.monthly_stats?.total_fijo || 0}</p>
            </div>
            <div className="p-4 rounded-lg bg-rose-500/10 border border-rose-500/20">
              <p className="text-xs font-semibold tracking-widest uppercase text-rose-400 mb-1">Inválidos</p>
              <p className="text-2xl font-bold text-rose-400">{selectedKeyUsage.monthly_stats?.total_invalido || 0}</p>
            </div>
          </div>

          {selectedKeyUsage.recent_usage?.length > 0 && (
            <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-6">
              <h3 className="text-lg font-bold text-white mb-4">Últimas Consultas</h3>
              <div className="rounded-lg border border-zinc-800 overflow-hidden">
                <table className="w-full text-sm" data-testid="usage-table">
                  <thead>
                    <tr className="bg-zinc-950 border-b border-zinc-800">
                      <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-zinc-500">Fecha</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-zinc-500">Endpoint</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-zinc-500">Teléfonos</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-zinc-500">Celulares</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-zinc-500">Fijos</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-zinc-500">Inválidos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedKeyUsage.recent_usage.map((u, i) => (
                      <tr key={i} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                        <td className="px-4 py-2.5 text-zinc-400 text-xs">{formatDate(u.timestamp)}</td>
                        <td className="px-4 py-2.5">
                          <Badge className="bg-violet-500/10 text-violet-400 text-xs">{u.endpoint}</Badge>
                        </td>
                        <td className="px-4 py-2.5 text-white font-mono text-xs">{u.phone_count}</td>
                        <td className="px-4 py-2.5 text-emerald-400 font-mono text-xs">{u.results_summary?.celular || 0}</td>
                        <td className="px-4 py-2.5 text-blue-400 font-mono text-xs">{u.results_summary?.fijo || 0}</td>
                        <td className="px-4 py-2.5 text-rose-400 font-mono text-xs">{u.results_summary?.invalido || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      <AlertDialog open={!!keyToDelete} onOpenChange={() => setKeyToDelete(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Eliminar API Key?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Se revocará la key de "{keyToDelete?.company_name}". Las integraciones externas dejarán de funcionar.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDeleteKey(keyToDelete?.key_id)} className="bg-rose-600 hover:bg-rose-700 text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
